import { sendAnalyticsInfo } from 'features/analytics/helpers';
import type { AppDispatch, RootState } from 'features/app/store/store';
import { isPositiveUserId } from 'features/call/call-base/helpers';
import {
  callLanguageCodeSelector,
  callIdSelector,
  callIdMatchesCurrentCall,
  coreServicesUserIdSelector,
  coreRingGroupOwnerUserIdSelector,
} from 'features/call/call-base/store/selectors';
import { selectDeafHasConnected } from 'features/call/call-deaf/store';
import {
  isEmergencyCallSelector,
  isEmergencyCallbackSelector,
} from 'features/call/vrs-call/store/vrsCallSelectors';
import { handleError } from 'features/notification/store';
import {
  normalizeGreetingPreference,
  resolveAstSignMailEligibility,
  type AstSignMailEligibility,
} from 'features/ast-signmail/helpers';
import {
  attemptAutoAstSignMailHandoff,
  astSignMailModeReceived,
} from 'features/ast-signmail/store';

import { signMailCoreResolvedGreetingPreferenceSelector } from './signMailSelectors';
import { preFetchedUploadInfoReceived } from './signMailSlice';

import { getLocalSignMailStartBlockReason } from 'features/signmail/helpers';
import type { VideoMessageData } from 'features/signmail/interfaces';
import { SignMailDaoService } from 'features/signmail/services';

const START_AUTO_SIGN_MAIL_METHOD = 'startAutoSignMailFlow';

export interface StartAutoSignMailRoutingContext {
  callId: number;
  callLanguageCode: string;
  hasDeafConnected: boolean;
  isEmergencyCall: boolean;
  isEmergencyCallback: boolean;
  resolvedGreetingPreference: ReturnType<typeof normalizeGreetingPreference>;
}

export interface StartAutoSignMailPrefetchResult {
  preloadedVideoMsgData?: VideoMessageData;
  astSignMailMode?: number;
  isMailboxFull: boolean;
}

const buildAstSignMailRoutingDetails = ({
  eligibility,
  routingContext,
  astSignMailMode,
  isMailboxFull,
}: {
  eligibility: AstSignMailEligibility;
  routingContext: StartAutoSignMailRoutingContext;
  astSignMailMode: number | undefined;
  isMailboxFull: boolean;
}) => {
  const { checks, ...eligibilitySummary } = eligibility;

  return {
    eligibility: eligibilitySummary,
    inputs: {
      greetingPreference: routingContext.resolvedGreetingPreference,
      callLanguageCode: routingContext.callLanguageCode || 'unknown',
      astSignMailMode: astSignMailMode ?? 'unknown',
      hasDeafConnected: routingContext.hasDeafConnected,
      isMailboxFull,
      isEmergencyCall: routingContext.isEmergencyCall,
      isEmergencyCallback: routingContext.isEmergencyCallback,
    },
    checks,
  };
};

/**
 * Collects the local state that influences whether auto SignMail should route
 * to AST or stay in the legacy SignMail flow.
 *
 * The result is intentionally derived before any async work starts so the
 * routing decision can be explained from one stable snapshot.
 */
export const buildAutoSignMailRoutingContext = (
  state: RootState
): StartAutoSignMailRoutingContext => ({
  callId: callIdSelector(state),
  callLanguageCode: callLanguageCodeSelector(state),
  hasDeafConnected: selectDeafHasConnected(state),
  isEmergencyCall: isEmergencyCallSelector(state),
  isEmergencyCallback: isEmergencyCallbackSelector(state),
  resolvedGreetingPreference: normalizeGreetingPreference(
    signMailCoreResolvedGreetingPreferenceSelector(state)
  ),
});

/**
 * Prefetches the external data needed by auto SignMail routing.
 *
 * Flow notes:
 * - Deaf-user info and upload-url info are requested in parallel to reduce the
 *   delay before either AST handoff or local SignMail can begin.
 * - Partial failure is tolerated. Missing AST data simply disables AST routing,
 *   while missing upload data skips the cache and allows the legacy flow to
 *   refetch later if needed.
 * - Successful responses are cached in Redux so later steps can reuse them
 *   instead of repeating the same calls.
 */
export const prefetchAutoSignMailRoutingData = async (
  state: RootState,
  dispatch: AppDispatch,
  getState: () => RootState,
  getDeafUserInformationRequest: (state: RootState) => {
    calleeNumber: string;
    callerNumber: string;
    blockCallerId: boolean;
  }
): Promise<StartAutoSignMailPrefetchResult> => {
  const callId = callIdSelector(state);
  let preloadedVideoMsgData: VideoMessageData | undefined;
  let astSignMailMode: number | undefined;
  let isMailboxFull = false;

  const [deafUserInfoResult, uploadUrlResult] = await Promise.allSettled([
    SignMailDaoService.getDeafUserInformation(
      getDeafUserInformationRequest(state)
    ),
    SignMailDaoService.getUploadUrl(callId),
  ]);

  if (!callIdMatchesCurrentCall(getState(), callId)) {
    return {
      isMailboxFull: false,
    };
  }

  if (deafUserInfoResult.status === 'fulfilled') {
    const response = deafUserInfoResult.value;
    preloadedVideoMsgData = response.VideoMsgData;
    // Core returns `1` when AST auto SignMail routing is enabled; treat a
    // missing value the same as disabled so eligibility checks stay explicit.
    astSignMailMode = response.ASTSignMailMode ?? 0;
    dispatch(astSignMailModeReceived(astSignMailMode));
    dispatch(
      sendAnalyticsInfo({
        Method: START_AUTO_SIGN_MAIL_METHOD,
        ASTSignMailMode: astSignMailMode,
        LogGroup: 'SignMail',
      })
    );
  } else {
    dispatch(
      handleError({
        error: deafUserInfoResult.reason,
        methodName: START_AUTO_SIGN_MAIL_METHOD,
      })
    );
  }

  if (uploadUrlResult.status === 'fulfilled' && uploadUrlResult.value) {
    const { UploadURL: uploadUrl, MailboxFull } = uploadUrlResult.value;
    isMailboxFull = Boolean(MailboxFull);
    dispatch(
      preFetchedUploadInfoReceived({
        uploadUrl,
        isMailboxFull,
      })
    );

    if (isMailboxFull) {
      dispatch(
        sendAnalyticsInfo({
          Method: START_AUTO_SIGN_MAIL_METHOD,
          message:
            'Mailbox full detected early; skipping AST, continuing with SignMail greeting.',
          LogGroup: 'SignMail',
        })
      );
    }
  } else if (uploadUrlResult.status === 'rejected') {
    dispatch(
      handleError({
        error: uploadUrlResult.reason,
        methodName: `${START_AUTO_SIGN_MAIL_METHOD}.getUploadUrl`,
        disableUserNotification: true,
      })
    );
  }

  return {
    preloadedVideoMsgData,
    astSignMailMode,
    isMailboxFull,
  };
};

/**
 * Attempts the AST branch of the auto SignMail flow.
 *
 * Flow notes:
 * - Missing or disabled AST inputs are treated as a normal fallback condition,
 *   not an exception.
 * - Eligibility is evaluated from the pre-resolved greeting, call language,
 *   AST mode, current-call connection history, and mailbox status.
 * - Even after the eligibility pass, local SignMail ownership is re-checked so
 *   AST does not start after another path has already claimed the session.
 *
 * @returns `true` when AST handoff started and local fallback should stop.
 */
export const tryStartEligibleAutoAstSignMailHandoff = async ({
  dispatch,
  getState,
  routingContext,
  astSignMailMode,
  isMailboxFull,
}: {
  dispatch: AppDispatch;
  getState: () => RootState;
  routingContext: StartAutoSignMailRoutingContext;
  astSignMailMode: number | undefined;
  isMailboxFull: boolean;
}): Promise<boolean> => {
  const { resolvedGreetingPreference } = routingContext;

  if (!resolvedGreetingPreference) {
    dispatch(
      sendAnalyticsInfo({
        Method: START_AUTO_SIGN_MAIL_METHOD,
        message:
          'AST SignMail routing skipped: no pre-resolved greeting preference available.',
        LogGroup: 'SignMail',
      })
    );
    return false;
  }

  const astEligibility = resolveAstSignMailEligibility(
    resolvedGreetingPreference,
    routingContext.callLanguageCode,
    astSignMailMode,
    {
      hasDeafConnected: routingContext.hasDeafConnected,
      isEmergencyCall: routingContext.isEmergencyCall,
      isEmergencyCallback: routingContext.isEmergencyCallback,
      isMailboxFull,
    }
  );

  dispatch(
    sendAnalyticsInfo({
      Method: START_AUTO_SIGN_MAIL_METHOD,
      message: 'AST SignMail routing decision from pre-resolved core data.',
      ...buildAstSignMailRoutingDetails({
        eligibility: astEligibility,
        routingContext,
        astSignMailMode,
        isMailboxFull,
      }),
      LogGroup: 'SignMail',
    })
  );

  const currentState = getState();
  const coreServicesUserIdFromCall = coreServicesUserIdSelector(currentState);
  const coreRingGroupOwnerUserId =
    coreRingGroupOwnerUserIdSelector(currentState);
  const hasCoreServicesUserId = isPositiveUserId(coreServicesUserIdFromCall);
  const hasRingGroupOwnerUserId = isPositiveUserId(coreRingGroupOwnerUserId);
  const isCallIdStale = callIdSelector(currentState) !== routingContext.callId;
  const localSignMailStartBlockReason =
    getLocalSignMailStartBlockReason(currentState);
  const canStartLocalSignMail = localSignMailStartBlockReason === null;

  if (!callIdMatchesCurrentCall(currentState, routingContext.callId)) {
    return false;
  }

  if (
    isCallIdStale ||
    (!hasCoreServicesUserId && !hasRingGroupOwnerUserId) ||
    !astEligibility.isAstEligible ||
    !canStartLocalSignMail
  ) {
    dispatch(
      sendAnalyticsInfo({
        Method: START_AUTO_SIGN_MAIL_METHOD,
        message:
          'AST SignMail routing eligible but handoff was not started due to final guard checks.',
        checks: {
          isCallIdStale,
          hasCoreServicesUserId,
          hasRingGroupOwnerUserId,
          isAstEligible: astEligibility.isAstEligible,
          canStartLocalSignMail,
          localSignMailStartBlockReason:
            localSignMailStartBlockReason ?? 'none',
        },
        LogGroup: 'SignMail',
      })
    );

    return false;
  }

  try {
    await dispatch(
      attemptAutoAstSignMailHandoff({
        coreServicesUserId: hasCoreServicesUserId
          ? coreServicesUserIdFromCall
          : null,
      })
    ).unwrap();
    return true;
  } catch {
    dispatch(
      sendAnalyticsInfo({
        Method: START_AUTO_SIGN_MAIL_METHOD,
        message:
          'AST SignMail handoff failed during ownership transfer; continuing with normal SignMail.',
        LogGroup: 'SignMail',
      })
    );
    return false;
  }
};
